'use client';
import ButtonBase from '@mui/material/ButtonBase';
import {styled, type SxProps, type Theme} from '@mui/material/styles';
import {useTranslation} from 'react-i18next';
import {AppIcon, FaChevronLeftIcon, FaChevronRightIcon} from '../icon';

export type NavArrowTone = 'light' | 'dark';

export interface NavArrowProps {
  direction: 'prev' | 'next';
  tone?: NavArrowTone;
  disabled?: boolean;
  onClick?: () => void;
  'aria-label'?: string;
  sx?: SxProps<Theme>;
  className?: string;
}

// [MASTER] Carousel Arrow — Figma node 550:21788, 40x40 pill.
const NAV_ARROW_SIZE = 40;

function arrowTokens(theme: Theme, tone: NavArrowTone) {
  const {surface, border, icon} = theme.palette.colors;
  return {
    light: {
      background: surface.neutral.default,
      border: border.neutral.subtle,
      icon: icon.neutral.strong,
      hover: surface.neutral.subtle,
      disabled: {backgroundColor: surface.neutral.disabled, borderColor: surface.neutral.disabled, color: icon.neutral.strong},
      focus: border.focus.default,
    },
    dark: {
      background: surface.neutral.inverseSubtle,
      border: 'transparent',
      icon: icon.neutral.inverse,
      hover: surface.neutral.inverseSubtleHover,
      disabled: {backgroundColor: surface.neutral.inverseSubtle, color: surface.neutral.inverseSubtle},
      focus: border.focus.onColor,
    },
  }[tone];
}

const Root = styled(ButtonBase, {shouldForwardProp: propName => propName !== 'oTone'})<{oTone: NavArrowTone}>(({theme, oTone}) => {
  const tokens = arrowTokens(theme, oTone);
  return {
    flexShrink: 0,
    width: NAV_ARROW_SIZE,
    height: NAV_ARROW_SIZE,
    // Pin the glyph size: a bare ButtonBase inherits no font-size, so without this
    // the em-sized icon collapses onto the browser's default <button> font-size.
    fontSize: 14,
    borderRadius: theme.radii.full,
    border: `1px solid ${tokens.border}`,
    backgroundColor: tokens.background,
    color: tokens.icon,
    '&:hover:not(:disabled)': {backgroundColor: tokens.hover},
    '&:disabled': {cursor: 'not-allowed', ...tokens.disabled},
    '&:focus-visible': {outline: `2px solid ${tokens.focus}`, outlineOffset: 2},
  };
});

export function NavArrow({direction, tone = 'light', sx, 'aria-label': ariaLabel, ...rest}: NavArrowProps) {
  const {t} = useTranslation();
  return (
    <Root type="button" oTone={tone} sx={sx} aria-label={ariaLabel ?? (direction === 'prev' ? t('previous') : t('next'))} {...rest}>
      <AppIcon icon={direction === 'prev' ? FaChevronLeftIcon : FaChevronRightIcon} fontSize="inherit" aria-hidden />
    </Root>
  );
}

NavArrow.displayName = 'NavArrow';
